import { type FC, Fragment, type ReactElement } from "react"

import { useMount } from "react-use"

import { NotificationsBus } from "@/3514/components/shared/notification-bus/notification-bus.component"
import { LazySidebarEntity } from "@/3514/entities/sidebar"
import {
    ActivityGuard,
    PostSessionGuard,
    ScrollToTopGuard,
    SocialLoginGuard,
    withProductTypeGuard
} from "@/3514/guards"
import { type TUserState, useAppLanguage, useLoggingUserSet, useUser } from "@/3514/hooks"
import { PostSessionModal, ReloadAppModal } from "@/3514/modals"
import { type TAppDispatcher, useAppDispatcher } from "@/3514/store/app"
import { isUserCoach, isUserManager } from "@/3514/utils"

import { Loadable } from "../shared/loadable"

import { AppRouter } from "./router"

const Main: FC = (): ReactElement => {
    const { isLoading, isLoaded, user }: TUserState = useUser()

    const appDispatcher: TAppDispatcher = useAppDispatcher()

    useMount((): void => {
        if (isUserCoach(user) || isUserManager(user)) appDispatcher.closeSidebar()
    })

    useAppLanguage()

    useLoggingUserSet()

    return (
        <Fragment>
            <SocialLoginGuard />

            <ScrollToTopGuard />

            <ActivityGuard />

            <PostSessionGuard />

            <Loadable
                component={LazySidebarEntity}
                isReady={isLoaded}
                isLoading={isLoading}
                loadingFallback={<Fragment />}
            />

            <NotificationsBus />

            <AppRouter />

            <PostSessionModal />

            <ReloadAppModal />
        </Fragment>
    )
}

const GuardedMain: FC = withProductTypeGuard(Main)

GuardedMain.displayName = "Main"

export { GuardedMain as Main }
